// Човешки прочит на складовите одит записи (доставка, изписване/бракуване,
// инвентаризация, прехвърляне, разнасяне на отчет, взаимозаменяеми продукти,
// складови настройки, склад по продукт, възстановяване на анулиран документ).
//
// Два капана определят формата на всичко тук:
//
//  1. **Тялото често го НЯМА.** Инвентаризацията и разнасянето на отчет минават
//     тавана от 10 000 знака и middleware-ът ги заменя с маркера
//     `__truncated__`. Затова всеки интерпретатор първо вади ид-то на документа
//     (от пътя, от отговора или от самия маркер) и показва поне него — „Документ
//     №" е несравнимо по-полезно от „няма подробен преглед".
//  2. **DELETE няма тяло изобщо** — ид-то е в пътя и това е цялата информация.
//
// Имената на складовите продукти/складове/доставчици идват от `useAuditLookups`,
// което ги дърпа само когато отвореният запис е складов.

import type { TFunction } from "i18next";
import type { AuditLogDetail } from "../../../../api/auditLogs";
import type { AuditLookups } from "./useAuditLookups";
import type { HumanStory, Line, Tone } from "./story";
import {
  emptyStory,
  asArr,
  asObj,
  asString,
  field,
  isTruncated,
} from "./story";
import { eur, qty, toNumber } from "./format";
import { INV, MODULE_PATHS, RES, TRUNCATED } from "./contract";

/** Ид-то на документа: от пътя (PUT/DELETE), иначе от отговора или от маркера. */
function documentId(detail: AuditLogDetail): number | null {
  const fromPath = detail.path.match(MODULE_PATHS.inventoryDoc)?.[2];
  if (fromPath) return Number(fromPath);
  const fromResponse = toNumber(field(detail.response_body, RES.id));
  if (fromResponse !== null) return fromResponse;
  // Отрязаният отговор пренася `id` върху самия маркер (виж _cap_size в BE).
  const marker = asObj(detail.response_body);
  if (marker?.[TRUNCATED.flag] === true) return toNumber(marker[RES.id]);
  return null;
}

/** Тонът на документ по метод: създаване/промяна носят тона на модула. */
function toneForMethod(method: string, base: Tone): Tone {
  return method.toUpperCase() === "DELETE" ? "remove" : base;
}

function addDocumentFacts(
  story: HumanStory,
  detail: AuditLogDetail,
  t: TFunction,
): void {
  const id = documentId(detail);
  if (id !== null) {
    story.facts.push({
      label: t("auditLogDetail.human.documentNo"),
      value: `№ ${id}`,
    });
  }
}

/** Ред „количество × продукт" със склада като мерна единица. */
function productLine(
  productId: unknown,
  quantity: unknown,
  lookups: AuditLookups,
  tone?: Tone,
): Line {
  return {
    qty: qty(quantity),
    name: lookups.warehouseProductName(productId),
    unit: lookups.warehouseProductUnit(productId) ?? undefined,
    tone,
  };
}

function pushIf(
  story: HumanStory,
  label: string,
  value: string | null | undefined,
  inline = false,
): void {
  if (value) story.facts.push({ label, value, inline });
}

export function interpretInventory(
  kind: string,
  detail: AuditLogDetail,
  lookups: AuditLookups,
  t: TFunction,
): HumanStory | null {
  const body = detail.request_body;
  const object = asObj(body);
  const method = detail.method.toUpperCase();
  const truncated = isTruncated(body);
  const lines = asArr(object?.[INV.lines]);
  const human = (key: string) => t(`auditLogDetail.human.${key}`);

  // Общото за всички документи: анулиране/изтриване и отрязано тяло дават
  // разказ само с ид-то — но това вече е разчетен запис, не „суров".
  const documentStory = (tone: Tone): HumanStory => {
    const story = emptyStory(kind, toneForMethod(method, tone));
    addDocumentFacts(story, detail, t);
    if (method === "DELETE") {
      story.note = human(
        kind === "invInterchange" ? "deletedGroupNote" : "cancelledDocumentNote",
      );
      return story;
    }
    if (truncated) {
      story.note = human("truncatedBodyNote");
      story.interpreted = story.facts.length > 0;
    }
    return story;
  };

  switch (kind) {
    case "invDelivery": {
      const story = documentStory("add");
      if (method === "DELETE" || truncated) return story;
      pushIf(story, human("warehouse"), lookups.warehouseName(object?.[INV.warehouseId]));
      pushIf(story, human("supplier"), lookups.supplierName(object?.[INV.supplierId]));
      pushIf(story, human("invoiceNumber"), asString(object?.[INV.invoiceNumber]));
      let total = 0;
      story.lines = lines.map((raw) => {
        const line = asObj(raw) ?? {};
        const quantity = toNumber(line[INV.quantity]);
        const unitPrice = toNumber(line[INV.unitPrice]);
        const out = productLine(
          line[INV.productId],
          quantity ?? line[INV.packagingsCount],
          lookups,
          "add",
        );
        if (quantity !== null && unitPrice !== null) {
          total += quantity * unitPrice;
          out.total = eur(quantity * unitPrice);
        }
        return out;
      });
      story.linesLabel = human("products");
      if (total > 0) {
        story.total = eur(total);
        story.totalLabel = human("deliveryValue");
      }
      story.note = asString(object?.[INV.note]) ?? undefined;
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invWriteoff": {
      const story = documentStory("remove");
      if (method === "DELETE" || truncated) return story;
      pushIf(story, human("warehouse"), lookups.warehouseName(object?.[INV.warehouseId]));
      story.facts.push({
        label: human("writeoffKind"),
        value:
          object?.[INV.kind] === INV.scrapKind
            ? human("writeoffKindScrap")
            : human("writeoffKindNormal"),
        tone: "remove",
      });
      pushIf(story, human("reason"), asString(object?.[INV.reason]));
      // Ред за рецепта носи ПОРЦИИ, не количество на продукт — затова името
      // идва от другия справочник и мерната единица нарочно липсва.
      story.lines = lines.map((raw) => {
        const line = asObj(raw) ?? {};
        if (line[INV.kind] === INV.recipeLineKind) {
          return {
            qty: qty(line[INV.quantity]),
            name: lookups.recipeName(line[INV.recipeId]),
            tone: "remove" as Tone,
          };
        }
        return productLine(line[INV.productId], line[INV.quantity], lookups, "remove");
      });
      story.linesLabel = human("products");
      story.note = asString(object?.[INV.comment]) ?? undefined;
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invStocktake": {
      const story = documentStory("info");
      if (method === "DELETE" || truncated) return story;
      pushIf(story, human("warehouse"), lookups.warehouseName(object?.[INV.warehouseId]));
      pushIf(story, human("reason"), asString(object?.[INV.reason]));
      const substitutions = asArr(object?.[INV.substitutions]).length;
      if (substitutions > 0) {
        story.facts.push({
          label: human("substitutions"),
          value: String(substitutions),
        });
      }
      story.lines = lines.map((raw) => {
        const line = asObj(raw) ?? {};
        return productLine(line[INV.productId], line[INV.counted], lookups);
      });
      story.linesLabel = human("countedProducts");
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invTransfer": {
      const story = documentStory("info");
      if (method === "DELETE" || truncated) return story;
      pushIf(
        story,
        human("fromWarehouse"),
        lookups.warehouseName(object?.[INV.fromWarehouseId]),
      );
      pushIf(
        story,
        human("toWarehouse"),
        lookups.warehouseName(object?.[INV.toWarehouseId]),
      );
      story.lines = lines.map((raw) => {
        const line = asObj(raw) ?? {};
        return productLine(line[INV.productId], line[INV.quantity], lookups);
      });
      story.linesLabel = human("products");
      story.note = asString(object?.[INV.note]) ?? undefined;
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invReportPosting": {
      const story = documentStory("info");
      if (method === "DELETE" || truncated) return story;
      const reportId = toNumber(object?.[INV.reportId]);
      if (reportId !== null) {
        story.facts.push({ label: human("reportNo"), value: `№ ${reportId}` });
      }
      const substitutions = asArr(object?.[INV.substitutions]).length;
      if (substitutions > 0) {
        story.facts.push({
          label: human("substitutions"),
          value: String(substitutions),
        });
      }
      story.lines = lines.map((raw) => {
        const line = asObj(raw) ?? {};
        return productLine(line[INV.productId], line[INV.quantity], lookups, "remove");
      });
      story.linesLabel = human("postedProducts");
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invInterchange": {
      const story = documentStory("neutral");
      if (method === "DELETE" || truncated) return story;
      pushIf(story, human("warehouse"), lookups.warehouseName(object?.[INV.warehouseId]));
      pushIf(story, human("hint"), asString(object?.[INV.hint]), true);
      story.lines = asArr(object?.[INV.members]).map((raw) => {
        const member = asObj(raw) ?? {};
        const coefficient = toNumber(member[INV.coefficient]);
        return {
          name: lookups.warehouseProductName(member[INV.productId]),
          subs:
            coefficient !== null
              ? [`${human("coefficient")}: ${qty(coefficient)}`]
              : undefined,
        };
      });
      story.linesLabel = human("groupMembers");
      story.interpreted = story.facts.length > 0 || story.lines.length > 0;
      return story;
    }

    case "invSettings": {
      const story = emptyStory(kind, "info");
      story.facts.push({
        label: human("vatRegistered"),
        value: object?.[INV.vatRegistered] === true ? human("yes") : human("no"),
      });
      pushIf(
        story,
        human("defaultWarehouse"),
        lookups.warehouseName(object?.[INV.defaultWarehouseId]),
      );
      return story;
    }

    case "invDeliveryNotes": {
      // Изваждане от фактура / приключване без фактура / връщане за фактуриране:
      // тялото е малко (`ids`, по избор `reason` и `payment`) — показваме всичко.
      const story = emptyStory(kind, "info");
      const action = detail.path.match(MODULE_PATHS.inventoryDeliveryNotes)?.[1];
      const ids = asArr(object?.[INV.ids])
        .map((value) => toNumber(value))
        .filter((value): value is number => value !== null);
      pushIf(story, human("deliveryNotes"), ids.map((id) => `№ ${id}`).join(", "));
      pushIf(story, human("closeReason"), asString(object?.[INV.reason]));
      const payment = asObj(object?.[INV.payment]);
      if (payment) {
        const paidAt = asString(payment[INV.paidAt]);
        const methodKey = asString(payment[INV.paymentMethod]);
        pushIf(
          story,
          human("markedPaid"),
          [
            paidAt ? paidAt.slice(0, 10).split("-").reverse().join(".") : null,
            methodKey ? t(`inventoryPage.payMethod_${methodKey}`) : null,
          ]
            .filter(Boolean)
            .join(" · ") || human("yes"),
        );
      }
      story.note = human(
        action === "detach-from-invoice"
          ? "detachedNotesNote"
          : action === "reopen-for-invoice"
            ? "reopenedNotesNote"
            : "closedNotesNote",
      );
      story.interpreted = ids.length > 0;
      return story;
    }

    case "invProductWarehouse": {
      const story = emptyStory(kind, toneForMethod(method, "info"));
      const productId = detail.path.match(MODULE_PATHS.inventoryProductWarehouse)?.[1];
      pushIf(story, human("product"), productId ? lookups.warehouseProductName(productId) : null);
      if (method === "DELETE") {
        story.note = human("productWarehouseRemovedNote");
        return story;
      }
      pushIf(story, human("productWarehouse"), lookups.warehouseName(object?.[INV.warehouseId]));
      return story;
    }

    case "invRestore": {
      const story = emptyStory(kind, "info");
      const restoredId = detail.path.match(MODULE_PATHS.inventoryRestore)?.[1];
      if (restoredId) {
        story.facts.push({
          label: human("restoredDocument"),
          value: `№ ${restoredId}`,
        });
      }
      story.note = human("restoredDocumentNote");
      return story;
    }

    default:
      return null;
  }
}
